import { AddButton } from '@/common/buttons/AddButton';
import { TitleButton } from '@/common/buttons/TitleButton';
import { useDialog } from '@/common/dialog/DialogContext';
import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { RelCreatorDialog } from '@/common/entityFramework/relCreator/RelCreatorDialog';
import { GridConstants } from '@/common/grid/GridConstants';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { StringUtils } from '@/common/utils/StringUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useCallback, useState } from 'react';
import { CustRelBulkDialog } from './CustRelBulkDialog';
import { CustRelExpireDialog } from './CustRelExpireDialog';

export const CustRelList = ({ custObj }) => {
  const custId = custObj?.id;
  const module = useModuleFromRoute();
  const { openDialog } = useDialog();

  // This mode tracks this customer's status as the Parent or Child of the relationships
  // Hence, the Tab names will be opposite of the mode
  const [mode, setMode] = useState('parent');
  const [selectedCustRels, setSelectedCustRels] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleChange = (event, newMode) => {
    if (newMode !== null) {
      setMode(newMode);
      setSelectedCustRels([]);
    }
  };

  const refresh = (changed) => {
    if (!changed) return;
    setSelectedCustRels([]);
    setRefreshKey((key) => key + 1);
  };

  const augRowsReq = useCallback(
    (rowsReq) => {
      if (!custId) return;
      const addlExpr = ExprUtils.whereParams(mode + 'CustId', custId);
      rowsReq.expr = ExprUtils.and(rowsReq.expr, addlExpr);
    },
    [custId, mode],
  );

  const augFieldDefs = useCallback((fieldToFieldDef) => {
    fieldToFieldDef.checkbox = GridConstants.checkbox;
  }, []);

  const handleSelectionChanged = (event) => {
    setSelectedCustRels(event.api.getSelectedRows());
  };

  // Augment nav FK's to match CustRel entity Parent / Child
  const augNavEntityObj = (navEntityObj) => {
    Object.keys(navEntityObj).forEach((key) => {
      navEntityObj[mode + StringUtils.capitalizeFirstLetter(key)] = navEntityObj[key];
      delete navEntityObj[key];
    });
  };

  // The picked side is the opposite of this Customer's own role in the relationship
  const pickedLabel = mode === 'parent' ? 'Children' : 'Parents';

  const addCustRels = () => {
    openDialog(RelCreatorDialog, {
      data: {
        module,
        entity: 'CUST',
        ...(mode === 'parent' ? { parentObj: custObj } : { childObj: custObj }),
      },
    }).then(refresh);
  };

  const pasteCustRels = () => {
    openDialog(CustRelBulkDialog, { custObj, mode, module }).then(refresh);
  };

  const expireCustRels = () => {
    openDialog(CustRelExpireDialog, { custRels: selectedCustRels, module }).then(refresh);
  };

  const augTabButtons = (tabButtons) => {
    ObjUtils.clearObj(tabButtons);
    tabButtons.push(
      <ToggleButtonGroup key="modeToggle" value={mode} exclusive onChange={handleChange}>
        <ToggleButton value="parent" aria-label="parent" data-test-id="toggle-parent">
          Children
        </ToggleButton>
        <ToggleButton value="child" aria-label="child" data-test-id="toggle-child">
          Parents
        </ToggleButton>
      </ToggleButtonGroup>,

      // Add button
      <AddButton key="add" entity="CUST_REL" augNavEntityObj={augNavEntityObj}></AddButton>,

      <TitleButton
        key="addMany"
        dataTestId="add-cust-rels"
        icon={fas.faUsers}
        toolTip={`Add ${pickedLabel}`}
        onClick={addCustRels}
      />,
      <TitleButton
        key="paste"
        dataTestId="paste-cust-rels"
        icon={fas.faClipboard}
        toolTip="Paste Relationships"
        onClick={pasteCustRels}
      />,
      <TitleButton
        key="expire"
        dataTestId="expire-cust-rels"
        icon={fas.faCalendarAlt}
        toolTip="Expire Selected"
        disabled={!selectedCustRels.length}
        onClick={expireCustRels}
      />,
    );
  };

  return (
    <EntityList
      key={mode}
      entity="CUST_REL"
      augTabButtons={augTabButtons}
      augRowsReq={augRowsReq}
      augFieldDefs={augFieldDefs}
      gridOptions={{ rowSelection: 'multiple' }}
      events={{ onSelectionChanged: handleSelectionChanged }}
      refreshTrigger={refreshKey}
    />
  );
};
